import React, { useState, useEffect, useCallback, useMemo } from "react";
import { motion } from "framer-motion";
import { ArrowDown, Github, Linkedin, Mail } from "lucide-react";
import { personalInfo } from "../../data/mock";
import { Button } from "../ui/button";

// Hook to detect mobile
const useIsMobile = () => {
  const [isMobile, setIsMobile] = useState(false);
  useEffect(() => {
    const mq = window.matchMedia("(max-width: 768px)");
    setIsMobile(mq.matches);
    const handler = (e) => setIsMobile(e.matches);
    mq.addEventListener("change", handler);
    return () => mq.removeEventListener("change", handler);
  }, []);
  return isMobile;
};

// Typing animation hook
const useTypingAnimation = (words, typingSpeed = 100, deletingSpeed = 60, pauseTime = 2000) => {
  const [currentWordIndex, setCurrentWordIndex] = useState(0);
  const [currentText, setCurrentText] = useState("");
  const [isDeleting, setIsDeleting] = useState(false);

  const animate = useCallback(() => {
    const currentWord = words[currentWordIndex];

    if (!isDeleting) {
      if (currentText.length < currentWord.length) {
        return setTimeout(() => {
          setCurrentText(currentWord.substring(0, currentText.length + 1));
        }, typingSpeed);
      } else {
        return setTimeout(() => setIsDeleting(true), pauseTime);
      }
    } else {
      if (currentText.length > 0) {
        return setTimeout(() => {
          setCurrentText(currentWord.substring(0, currentText.length - 1));
        }, deletingSpeed);
      } else {
        setIsDeleting(false);
        setCurrentWordIndex((prev) => (prev + 1) % words.length);
      }
    }
  }, [currentText, isDeleting, currentWordIndex, words, typingSpeed, deletingSpeed, pauseTime]);

  useEffect(() => {
    const timeout = animate();
    return () => clearTimeout(timeout);
  }, [animate]);

  return currentText;
};

// Aurora-style gradient background
const AuroraBackground = ({ darkMode, isMobile }) => {
  const orbs = useMemo(() => [
    { color: darkMode ? "bg-indigo-500/[0.07]" : "bg-indigo-400/[0.08]", size: "w-[500px] h-[500px]", pos: "top-1/4 -left-48", blur: "blur-[120px]", delay: 0 },
    { color: darkMode ? "bg-violet-500/[0.05]" : "bg-violet-400/[0.06]", size: "w-[400px] h-[400px]", pos: "bottom-1/4 -right-32", blur: "blur-[100px]", delay: 2 },
    { color: darkMode ? "bg-blue-500/[0.04]" : "bg-blue-400/[0.05]", size: "w-[350px] h-[350px]", pos: "top-1/2 left-1/3", blur: "blur-[100px]", delay: 4 },
  ], [darkMode]);

  if (isMobile) {
    return (
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-1/4 -left-16 w-48 h-48 rounded-full blur-[80px] ${
          darkMode ? "bg-indigo-500/[0.06]" : "bg-indigo-400/[0.08]"
        }`} />
        <div className={`absolute bottom-1/3 -right-16 w-40 h-40 rounded-full blur-[60px] ${
          darkMode ? "bg-violet-500/[0.04]" : "bg-violet-400/[0.06]"
        }`} />
      </div>
    );
  }

  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {orbs.map((orb, i) => (
        <motion.div
          key={i}
          className={`absolute rounded-full ${orb.color} ${orb.size} ${orb.pos} ${orb.blur}`}
          animate={{
            scale: [1, 1.1, 1],
            opacity: [0.6, 1, 0.6],
          }}
          transition={{
            duration: 8,
            repeat: Infinity,
            delay: orb.delay,
            ease: "easeInOut",
          }}
        />
      ))}
    </div>
  );
};

const HeroSection = ({ darkMode }) => {
  const isMobile = useIsMobile();
  const typingText = useTypingAnimation([
    "Software Developer",
    "IoT Enthusiast",
    "Problem Solver",
    "Tech Explorer",
  ]);

  const handleScrollToProjects = (e) => {
    e.preventDefault();
    document.querySelector("#projects")?.scrollIntoView({ behavior: "smooth" });
  };

  const fadeUp = isMobile
    ? { initial: { opacity: 0 }, animate: { opacity: 1 }, transition: { duration: 0.3 } }
    : { initial: { opacity: 0, y: 20 }, animate: { opacity: 1, y: 0 } };

  return (
    <section
      id="home"
      className={`relative min-h-screen flex items-center justify-center overflow-hidden ${
        darkMode ? "bg-[#09090b]" : "bg-gray-50/50"
      }`}
    >
      <AuroraBackground darkMode={darkMode} isMobile={isMobile} />

      {/* Subtle grid pattern — desktop only */}
      {!isMobile && (
        <div
          className={`absolute inset-0 ${
            darkMode ? "opacity-[0.025]" : "opacity-[0.03]"
          }`}
          style={{
            backgroundImage: `linear-gradient(${darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"} 1px, transparent 1px), linear-gradient(90deg, ${darkMode ? "rgba(255,255,255,0.08)" : "rgba(0,0,0,0.06)"} 1px, transparent 1px)`,
            backgroundSize: "64px 64px",
          }}
        />
      )}

      <div className="relative z-10 max-w-3xl mx-auto px-4 sm:px-6 text-center">
        {/* Status badge */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex justify-center mb-6 sm:mb-8"
        >
          <div className={`inline-flex items-center gap-2 px-4 py-1.5 rounded-full text-xs font-medium border backdrop-blur-sm ${
            darkMode
              ? "bg-indigo-500/[0.06] border-indigo-500/20 text-indigo-300"
              : "bg-indigo-50/80 border-indigo-200/60 text-indigo-600"
          }`}>
            <span className="w-1.5 h-1.5 rounded-full bg-indigo-400 animate-pulse" />
            Open to opportunities
          </div>
        </motion.div>

        {/* Name */}
        <motion.h1
          initial={{ opacity: 0, y: isMobile ? 10 : 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: isMobile ? 0.3 : 0.7, delay: 0.3 }}
          className={`text-4xl sm:text-5xl md:text-7xl font-bold tracking-tight mb-3 sm:mb-4 ${
            darkMode ? "text-white" : "text-gray-900"
          }`}
          style={{ fontFamily: "'Inter', sans-serif" }}
        >
          {personalInfo.name}
        </motion.h1>

        {/* Typing animation */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="h-10 flex items-center justify-center mb-4 sm:mb-6"
        >
          <span className={`text-base sm:text-lg md:text-xl font-light ${
            darkMode ? "text-white/35" : "text-gray-400"
          }`}>
            I'm a{" "}
          </span>
          <span className="text-base sm:text-lg md:text-xl font-medium text-indigo-400 ml-1.5">
            {typingText}
          </span>
          <span className="w-0.5 h-6 bg-indigo-400 ml-0.5 animate-pulse" />
        </motion.div>

        {/* Description */}
        <motion.p
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.7 }}
          className={`max-w-xl mx-auto text-sm md:text-[15px] leading-relaxed mb-8 sm:mb-10 ${
            darkMode ? "text-white/35" : "text-gray-500"
          }`}
        >
          {personalInfo.shortBio}
        </motion.p>

        {/* CTA Buttons */}
        <motion.div
          {...fadeUp}
          transition={{ duration: 0.5, delay: 0.9 }}
          className="flex flex-col sm:flex-row items-center justify-center gap-3 mb-10 sm:mb-12 w-full"
        >
          <Button
            onClick={handleScrollToProjects}
            className="bg-indigo-500 hover:bg-indigo-600 text-white rounded-xl px-6 h-12 sm:h-11 text-sm font-medium shadow-lg shadow-indigo-500/20 transition-all duration-300 hover:shadow-indigo-500/30 hover:scale-[1.02] active:scale-[0.98] w-full sm:w-auto"
          >
            View Projects
          </Button>
          <a href={personalInfo.resumeUrl} target="_blank" rel="noopener noreferrer" className="w-full sm:w-auto">
            <Button
              variant="outline"
              className={`rounded-xl px-6 h-12 sm:h-11 text-sm font-medium transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] w-full sm:w-auto ${
                darkMode
                  ? "border-white/[0.08] text-white/60 hover:bg-white/[0.04] hover:text-white/80 hover:border-white/[0.12]"
                  : "border-gray-200 text-gray-500 hover:bg-gray-50 hover:text-gray-700"
              }`}
            >
              Download Resume
            </Button>
          </a>
        </motion.div>

        {/* Social Links */}
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 0.5, delay: 1.1 }}
          className="flex items-center justify-center gap-3"
        >
          {[
            { icon: Github, href: personalInfo.social.github, label: "GitHub" },
            { icon: Linkedin, href: personalInfo.social.linkedin, label: "LinkedIn" },
            { icon: Mail, href: `mailto:${personalInfo.email}`, label: "Email" },
          ].map(({ icon: Icon, href, label }) => (
            <a
              key={label}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              aria-label={label}
              className={`w-11 h-11 sm:w-10 sm:h-10 rounded-xl flex items-center justify-center transition-all duration-300 border ${
                darkMode
                  ? "border-white/[0.04] text-white/25 hover:text-indigo-400 hover:border-indigo-500/20 hover:bg-indigo-500/[0.06]"
                  : "border-gray-200 text-gray-350 hover:text-indigo-500 hover:border-indigo-200 hover:bg-indigo-50"
              }`}
            >
              <Icon size={17} />
            </a>
          ))}
        </motion.div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
        <ArrowDown size={18} className={darkMode ? "text-white/15" : "text-gray-300"} />
      </div>
    </section>
  );
};

export default HeroSection;
